import { Controller } from '@hotwired/stimulus';

import { MINUS_SIGN, delayShort } from '../utils/planning_delay_notation.js';
import { workCalendarFrom } from '../utils/planning_work_calendar.js';

// FU-A: what a NEW lot takes from a choice that has no `parentPrefill` entry (the root, a
// lot the form cannot choose). A selectable PARENT lot has its own entry (CR-H4).
const NOTHING_TAKEN = Object.freeze({ progress: '0', start: '', end: '', color: '' });

// French typography: a no-break space before « % » (the preview's server rendering).
const NBSP = ' ';

// Délai entre lots (T6): the link type of a prerequisite checked on the fly (no
// `incomingLinks` entry yet) — the type `LinkDependencyService` creates by default.
const DEFAULT_LINK_TYPE = 'fs';
const DELAY_UNITS = Object.freeze(['worked', 'calendar']);
const DEFAULT_DELAY_UNIT = 'worked';
// A typed delay: optional sign, digits (Q-DL-3: the typographic « − » is read as « - »).
const DELAY_PATTERN = /^[+-]?\d+$/;
// `PhaseDependency::DELAY_RANGE` (±365): beyond it the server refuses the delay, and the
// calendar maths would overflow a JS Date.
const DELAY_MAX = 365;

// `%{name}` placeholders of a server sentence. The sentence is SPLIT on them, never
// interpolated into markup: each piece becomes a text node or an element filled by
// `textContent` (a lot name is user data, piège #591). A capturing split keeps the
// placeholders as their own tokens.
const PLACEHOLDER = /(%\{\w+\})/;
const PLACEHOLDER_NAME = /^%\{(\w+)\}$/;

// A server sentence with its `%{name}` placeholders replaced by PLAIN TEXT (the result
// only ever lands in `textContent`); an unknown placeholder stays as written.
const fillPlaceholders = (template, values) =>
  template.replace(/%\{(\w+)\}/g, (match, name) => (name in values ? values[name] : match));

// FU-A: a `parentPrefill` entry or `prefillTaken`, as the four comparable strings.
function prefillFields(raw) {
  return {
    progress: String(raw?.progress ?? '0'),
    start: raw?.start ?? '',
    end: raw?.end ?? '',
    color: raw?.color ?? '',
  };
}

// Progress values compare as numbers (« 40 » = « 40.0 »); an empty or invalid field
// never matches (the user cleared it: kept).
function sameNumber(left, right) {
  const value = Number.parseFloat(left);
  return Number.isFinite(value) && value === Number.parseFloat(right);
}

// NR1-01: a « Durée » still holding the value taken from a parent. Two blanks match (no
// span was taken, none was typed); otherwise compared as numbers.
function sameDuration(left, right) {
  if (String(left ?? '') === '' && String(right ?? '') === '') return true;
  return sameNumber(left, right);
}

/**
 * planning-phase-form — keeps a phase's « Durée » (work_days) and « Fin » (end
 * date) in a live, BIDIRECTIONAL lock-step as the user types, plus refreshes the
 * « Entreprise » hint when the marché select changes.
 *
 * The span is anchored on the START date; « Durée » and « Fin » are two views of
 * the same span, kept in sync in the browser without a round-trip:
 *   • edit Début or Durée  → recompute Fin  = the Durée-th WORKED day from Début (recomputeEnd)
 *   • edit Fin             → recompute Durée = worked days of [Début, Fin]    (recomputeDuration)
 * All math is in the site's WORKED days (PR C), through the pure module
 * `utils/planning_work_calendar.js`, twin of `Plans::WorkCalendar` (parity test):
 * the SAME arithmetic as the server's fallback (`LotForm#effective_end_date`).
 * « Fin » is an EDITABLE, AUTHORITATIVE field: the server uses the submitted end
 * date directly (and re-validates « Fin ≥ Début »), falling back to work_days only
 * when it is blank. Dates stay ISO strings end to end (UTC-midnight arithmetic),
 * never local `Date`s.
 *
 * ── JOURS TRAVAILLÉS (PR C) ────────────────────────────────────────────────
 *   • « Durée » may be 0 (RV-1): a phase posed entirely on non-worked days is worth
 *     0 worked days. Durée = 0 never blanks an existing Fin (the phase stays
 *     renamable), and a Fin moved onto a weekend reads 0, never a forced 1.
 *   • A server-rendered Fin is NEVER rewritten on connect: an existing phase ending
 *     on a Saturday (Mon → Sat = 5 worked days) would otherwise move to the Friday
 *     the moment the drawer opens. `connect` recomputes only an EMPTY Fin.
 *   • A date posed on a non-worked day is ACCEPTED, with a non-blocking warning
 *     line per date (`#refreshWarnings`), cloned from a server `<template>` and
 *     filled with `textContent` from the locale sentence (`nonWorkedWarning`,
 *     `%{date}` / `%{weekday}`); the field's wrapper gets `data-non-worked`
 *     (border colour in CSS, never in JS — contract #18).
 *
 * `start` and `endDate` are now the HIDDEN ISO inputs (`data-date-picker-target="hidden"`)
 * of the calendar `Form::DateInputComponent` (ISO YYYY-MM-DD). The lock-step listens to
 * `change` (the sibling `date-picker` controller dispatches it on every pick / valid-blur
 * commit), and every write goes through `#setIso`, which dispatches a self-suppressed
 * bubbling `change` so the picker re-syncs its FR display + popup (`#onHiddenChange`).
 *
 * « Fin ≥ Début » (minimum 1 day) is enforced two ways (the hidden input has no native
 * `min` floor): an earlier Fin snaps back to Début + Durée 1 in `recomputeDuration`, and
 * the server validates the pair (« Fin ≥ Début »).
 *
 * recomputeEnd ↔ recomputeDuration never ping-pong: `#setIso` raises `_suppress` around
 * its own `change` dispatch, so the opposite field's recompute handler treats OUR
 * programmatic write as a no-op (mirrors the picker's `_internalWrite` idiom).
 *
 * It also keeps the « Entreprise : <nom> » hint in sync with the chosen marché
 * (Feedback Gantt #38): the `marcheContractors` value maps each <option> value →
 * entreprise name (nil for « Aucun » / contractor-less marchés); `updateContractor`
 * reveals + fills the hint, or hides it when the marché has no entreprise.
 *
 * It PRE-FILLS the « Marché » select from the « Lot parent » select: when the
 * parent lot changes, `syncMarcheFromParent` reads the lot's INHERITED marché (its
 * own, else the first ancestor group's) from the `parentMarche` map and writes it
 * into the marché select, then refreshes the
 * entreprise hint. Setting `<select>.value` in JS does NOT fire a `change` event,
 * so the hint refresh is invoked DIRECTLY (never relied on the marché listener).
 *
 * ── ENTREPRISE REPRISE DU PARENT (DR-1, QA-PR2-35) ─────────────────────────
 * A NEW lot carries a hidden `phase[owner_id]` (`inheritedOwner`), server-rendered
 * with the OWN entreprise of the pre-filled parent when that parent has no inherited
 * marché (E Q17: only the form pre-fills, the server copies nothing). On a « Lot
 * parent » change, `#syncInheritedOwner` rewrites it from the `parentOwner` map (''
 * when the new parent has no own entreprise or inherits a marché) and keeps the
 * « Entreprise » label shown for « Aucun » marché in step (`#contractorNameFor('')`).
 * The hidden field follows the PARENT only, never the marché: a chosen marché wins on
 * the server (`LotForm#resolved_owner`, marché first), and clearing the field there
 * would lose the parent's entreprise if the user then comes back to « Aucun ».
 *
 * ── LOT PARENT (refonte PR 2, Task 7) ──────────────────────────────────────
 * « Lot parent » is a `Form::SearchableSelectComponent`: its HIDDEN input emits the
 * `change` (the search field's own `change`, a typed text, is ignored). On a NEW
 * sub-lot opened under a leaf, the « Premier sous-lot » banner (B03-02) names the
 * CHOSEN leaf (its sentence comes from `parentPrefill[id].banner`, server-composed):
 * hidden once a non-leaf is chosen (S-04), shown again with the right text for any
 * leaf. Without a sentence in the map (edit form, older markup) it follows the
 * pre-filled parent only (S-04 fallback).
 *
 * ── REPRISE DU PARENT CHOISI (FU-A, DR-1, B06 ; CR-H4, NR1-01) ─────────────
 * The first sub-lot of a LEAF takes that leaf's progress, dates and colour. On a
 * « Lot parent » change of a NEW lot (`prefillTaken` present), `#syncParentPrefill`
 * re-applies the prefill of the CHOSEN parent, read from `parentPrefill`: a leaf's
 * values, or (CR-H4) for a selectable PARENT lot (`parent: true`) what the « + » under
 * it gives — progress 0, Début = its `next_child_start` (FU-L), no Fin, its colour.
 * The root (no entry) maps to « nothing taken »: progress 0, no dates, default colour:
 *   • RULE « champ modifié gardé » : a field (progress, Début, Fin, colour) is
 *     rewritten ONLY if it still holds the value taken from the PREVIOUS parent
 *     (the first paint's `prefillTaken`, then each parent's values as they are
 *     applied). A value the user typed or picked is kept. Progress compares as a
 *     number, dates as ISO strings, colour as the checked radio's value.
 *   • NR1-01 — a DERIVED « Fin » (computed by `recomputeEnd` from Début + Durée) is
 *     not a saisie: the user's input is the Début and/or the Durée. On a parent
 *     change, a Durée still holding the previous parent's span follows the new one,
 *     the Début follows the rule above, and the Fin is recomputed from both.
 *   • The « Premier sous-lot » banner says « il reprend ses dates » only while BOTH
 *     dates are the chosen leaf's (`banner`); otherwise its server-composed variant
 *     without the dates (`bannerNoDates`), on a parent change AND as the user types.
 *   • `phase[prefilled_progress]` (QA-PR2-38) is NOT a user field: it is always
 *     the chosen LEAF's progress, '' otherwise (the server compares it with the
 *     posted progress under the plan lock).
 *   • Writes go through each field's own public path, never its logic: progress →
 *     the number input's value + a bubbling `input` (`range-input#sync` mirrors the
 *     slider); dates → `#setIso` (the `date-picker` resyncs), then « Durée » is
 *     recomputed (or blanked when a date is left alone); colour → the matching
 *     `phase[color]` radio is checked and gets a bubbling `change` (the
 *     `color-picker#select` action = a click on that swatch), or, for a colour
 *     with no swatch, the « Code couleur » field gets the hex + `input`
 *     (`color-picker#typeCode` = typing it).
 *   • « Repris de … » (`progressFrom`) shows the chosen leaf's sentence while the
 *     progress holds that leaf's value; the collapsed « Avancement » summary
 *     follows a rewritten progress.
 *
 * ── RE-ANCRAGE DES CALENDRIERS (F3) ────────────────────────────────────────
 * The server renders each date field's OPENING MONTH as
 * `data-date-picker-anchor-value` on its `date-picker` root. This controller keeps
 * that landmark fresh while the user fills the form:
 *   • « Lot parent » changed → every date root in this form is re-anchored on the
 *     new lot's anchor (`parentAnchor[id]`), falling back to the plan's
 *     advancement front (`planAnchor`). CREATION ONLY — see `#isCreation`.
 *   • « Début » typed        → the « Fin » root follows the start's month.
 * An anchor is a LANDMARK, never a value: the `date-picker` side never feeds it to
 * `selectedDates` and ignores it outright once the field holds an ISO, so
 * re-anchoring can NOT rewrite a date — it only moves which month opens.
 *
 * ── MILESTONE (jalon, C2) ──────────────────────────────────────────────────
 * On a jalon the form also carries an AUTONOMOUS « Acteur » select, kept in
 * lock-step with the marché:
 *   • marché chosen  → the Acteur select is LOCKED to the marché's entreprise
 *     (`#lockActorToMarche`): disabled, value mirrored into the hidden field, the
 *     « Acteur dérivé du marché » hint revealed.
 *   • marché cleared → the Acteur select is UNLOCKED (free choice, reset to
 *     « Aucun »), the hint hidden.
 * (The « Entreprise » validation checkbox and its `updateActor` visibility sync
 * left with the per-sub-lot validation — refonte PR 1, P10, A09-09.)
 *
 * All milestone targets are OPTIONAL — the phase/group form never renders them, so
 * every branch is guarded by `has…Target` and a non-jalon form is untouched.
 *
 * Targets:
 *   • start          — HIDDEN ISO <input type="hidden"> (`data-date-picker-target="hidden"`,
 *                      phase start, yyyy-mm-dd; the visible FR field is the picker's display)
 *   • workDays       — <input type="number"> (« Durée », worked days, min 0)
 *   • endDate        — HIDDEN ISO <input type="hidden"> (`data-date-picker-target="hidden"`,
 *                      « Fin », bidirectional, yyyy-mm-dd)
 *   • contractorBox  — the « Entreprise » hint container (revealed/hidden live)
 *   • contractorName — the <strong> holding the entreprise name
 *   • marche         — the « Marché » <select> (re-filled on a parent change)
 *   • actorSelect    — (jalon) the AUTONOMOUS « Acteur » <select> (phase[owner_id])
 *   • actorHidden    — (jalon, LOCKED only) hidden mirror of phase[owner_id] (a
 *                      disabled <select> is not POSTed)
 *   • actorLockedHint— (jalon) « Acteur dérivé du marché sélectionné. » <p>
 *   • warnings        — (PR C) the aria-live container of the non-worked warning lines
 *   • warningTemplate — (PR C) <template> of ONE warning line (icon + [data-warning-text])
 *   • milestoneDate   — (RV-40) a milestone's single hidden « Date » (same warning rule)
 *   • firstSubLotBanner — (Task 7, OPTIONAL) the « Premier sous-lot » banner wrapper
 *   • inheritedOwner    — (QA-PR2-35, NEW lot only) hidden `phase[owner_id]`: the
 *                         parent's own entreprise, rewritten on a « Lot parent » change
 *   • firstSubLotText   — (FU-A, OPTIONAL) the banner sentence (`textContent`)
 *   • progress          — (FU-A) the « Avancement » NUMBER input of `Form::RangeSliderComponent`
 *   • prefilledProgress — (FU-A, NEW lot only) hidden `phase[prefilled_progress]`
 *   • progressSummary   — (FU-A) the collapsed « Avancement » summary (« 40 % »)
 *   • progressFrom      — (FU-A, NEW lot only) « Repris de … » under « Avancement »
 *   • undatedHint       — (QA 03/10, edit of a leaf) « Sans dates, le lot est à
 *                         planifier. », shown (`hidden` attribute) only while Début AND
 *                         Fin are empty
 *   • earliest          — (T6, OPTIONAL) the « au plus tôt » notice, INSIDE an always-present
 *                         `aria-live="polite"` wrapper; shown (`hidden` attribute) only
 *                         while the lot starts too early
 *   • earliestText      — (T6) the notice's sentence: text nodes + the date in a
 *                          <strong>, built node by node (QA-F7), never innerHTML
 *   • delayRow          — (T6) one `<tr>` per prerequisite of the delay table
 *                         (`data-predecessor-id`, `data-predecessor-label`, `data-pred-start`,
 *                         `data-pred-end`, fields `[data-delay-field="days|unit|label"]`);
 *                         a row cloned by `planning-dependency-picker` is a target too
 * The colour picker's preview percentage (`[data-color-picker-preview-progress]`, a
 * `Form::ColorPickerComponent` hook, no target) follows « Avancement »
 * (`refreshProgressPreview`, QA 03/10: it always read « 40 % »).
 *
 * Values:
 *   • marcheContractors (Object) — { marché option value => entreprise name | null }
 *   • parentMarche      (Object) — { parent lot id => its INHERITED marché option value:
 *                       the lot's own, else the first ancestor group's ('' = none) }
 *   • parentAnchor      (Object) — (F3) { parent lot id (STRING) => ISO opening date }.
 *                       A lot with no dated phase has NO key (never '') → the plan
 *                       anchor takes over. ⚠️ Keys are STRINGS on purpose (the
 *                       builder `.to_s`-es the lot ids, mirroring `parentMarche`);
 *                       a numeric lookup misses SILENTLY and everything quietly
 *                       falls back to `planAnchor`, with every server test still green.
 *   • planAnchor        (String) — (F3) the plan's advancement front (end of its last
 *                       phase), '' when the plan has no dated phase — in which case
 *                       NO anchor is emitted at all and VanillaCalendar keeps its
 *                       native current-month default. We never fabricate "today".
 *   • calendar          (Object) — (plan 2026-10-29, T3) the site's FULL calendar,
 *                                   `Plans::WorkCalendar#as_json` = { weekdays, holidays,
 *                                   periods: [{ id, label, start, end }] }, read by
 *                                   `workCalendarFrom` (absent → {} → lun-ven, no exception)
 *   • nonWorkedWarning  (String) — (PR C) the locale sentence, `%{date}` / `%{weekday}` raw:
 *                                   a date outside the weekly mask
 *   • holidayWarning    (String) — (SS10) « Le %{date} est férié (%{name}). », raw
 *   • periodWarning     (String) — (SS10) « Le %{date} est dans « %{label} ». », raw
 *   • holidayNames      (Object) — (SS10) { holiday key => its locale name }
 *   • monthNames        (Array)  — (SS10) `date.month_names` of the locale (0 = null)
 *   • firstOfMonth      (String) — (SS10) the day-1 ordinal of the locale (« 1er »)
 *   • periodDefaultLabel (String) — (SS10) the name of a period saved without a label
 *   • zeroDayWarning    (String) — (RV-40, SS2) « 0 jour travaillé : … », shown when Début → Fin
 *                                   holds no worked day
 *   • weekdayNames      (Array)  — (PR C) `date.day_names` of the locale (0 = dimanche)
 *   • prefillParent     (String) — (Task 7) the parent lot id selected at first paint
 *                                   ('' = root); the banner belongs to it
 *   • parentOwner       (Object) — (QA-PR2-35) { lot id (STRING) => { id: ProjectActor id
 *                                   (String), label } } for the lots carrying their OWN
 *                                   entreprise without an inherited marché; any other lot
 *                                   has no key (→ hidden field '' and no « Aucun » label)
 *   • parentPrefill     (Object) — (FU-A) { selectable lot id (STRING) => { progress, start,
 *                                   end, color, from, banner, bannerNoDates } } for a LEAF,
 *                                   { progress, start, end: '', color, parent: true } for a
 *                                   PARENT lot (CR-H4); strings ('' = none); `from`,
 *                                   `banner` and `bannerNoDates` are server-composed sentences
 *   • prefillTaken      (Object) — (FU-A, NEW lot only) { progress, start, end, color }: what
 *                                   the first paint took (the « still taken » baseline);
 *                                   absent ⇒ no value is ever rewritten
 *   • incomingLinks     (Array)  — (T6) EVERY incoming link of the edited lot, milestones
 *                                   included (R-22): { predecessorId (String), type
 *                                   (fs|ss|ff|sf), delayDays, delayUnit, predecessorLabel,
 *                                   predStart, predEnd (ISO or null) }
 *   • shortDayNames     (Array)  — (T6) abbreviated day names WITH their dot (0 = « dim. »)
 *   • delayLabels       (Object) — (T6) the i18n hash `planning.delay`, for the shared
 *                                   formatter `utils/planning_delay_notation.js`
 *   • earliestSentence  (String) — (T6) « Au plus tôt le %{date}, après %{lot} », raw: the
 *                                   sentence for a NULL delay (D18, retour Nicolas 16/10)
 *   • earliestSentenceWithDelay (String) — « Au plus tôt le %{date}, après %{lot} %{delay} »,
 *                                   raw: any other delay
 *
 * ── AU PLUS TÔT (délai entre lots, T6 ; D12, R-22, S1, S2, S7) ─────────────
 * `refreshEarliest` (on connect, after every date / Durée change, on any `input` /
 * `change` of a `[data-delay-field]` and on `planning-dependency-picker:changed`, the
 * last two listened to on `this.element`, no `data-action` needed) computes, for each
 * incoming link, the date it imposes with `constrainedDate` (site calendar,
 * `calendar`), and keeps the MOST demanding proposed start:
 *   • links read from the VISIBLE delay rows, with the delay and unit TYPED there (empty
 *     = 0, « −2 » = « -2 »), the type from `incomingLinks` (`fs` for a prerequisite
 *     checked on the fly); a HIDDEN row (unchecked) removes its link;
 *   • links WITHOUT a row (a milestone → lot link, S7) from `incomingLinks` as they are;
 *   • fs / ss bound the START; ff / sf bound the END, and propose
 *     `startFor(required end, Durée)`;
 *   • a predecessor without the anchoring date is ignored (S2).
 * The lot is too early when `nextWorked(Début) < proposed start`; the notice then names
 * that link (`delayShort`, the shared formatter, only when its delay is not 0) and the date
 * (`shortDayNames`). Never
 * blocking. `snapToEarliest` (« Caler à cette date ») writes that start through
 * `#setIso`, recomputes « Fin » at constant worked Durée (`recomputeEnd`, the existing
 * Durée → Fin path) and moves the focus to Début's visible field.
 *
 * Actions:
 *   • recomputeEnd         — fired on `change` from `start` (hidden ISO) + `input` from
 *                            `workDays`; also on connect.
 *   • recomputeDuration    — fired on `change` from `endDate` (hidden ISO).
 *   • refreshProgressPreview — fired on `input` of the « Avancement » number AND slider.
 *   • updateContractor     — fired on `change` of the marché select.
 *   • syncMarcheFromParent — fired on `change` of the « Lot parent » hidden input
 *                            (bubbled to its wrapper).
 *   • refreshEarliest      — (T6) public; wired internally (see « AU PLUS TÔT »).
 *   • snapToEarliest       — (T6) `click` of « Caler à cette date ».
 *
 * @example
 *   <div data-controller="planning-phase-form"
 *        data-planning-phase-form-marche-contractors-value='{"":null,"7":"Acme"}'
 *        data-planning-phase-form-parent-marche-value='{"3":"7","9":""}'>
 *     <select data-action="change->planning-phase-form#syncMarcheFromParent">…</select>
 *     <select data-planning-phase-form-target="marche"
 *             data-action="change->planning-phase-form#updateContractor">…</select>
 *     <div data-planning-phase-form-target="contractorBox">
 *       Entreprise : <strong data-planning-phase-form-target="contractorName">Acme</strong>
 *     </div>
 *     <input type="hidden" data-planning-phase-form-target="start"
 *            data-date-picker-target="hidden"
 *            data-action="change->planning-phase-form#recomputeEnd">
 *     <input type="number" data-planning-phase-form-target="workDays"
 *            data-action="input->planning-phase-form#recomputeEnd">
 *     <input type="hidden" data-planning-phase-form-target="endDate"
 *            data-date-picker-target="hidden"
 *            data-action="change->planning-phase-form#recomputeDuration">
 *   </div>
 */
export default class extends Controller {
  static targets = [
    'start',
    'workDays',
    'endDate',
    'contractorBox',
    'contractorName',
    'marche',
    'actorSelect',
    'actorHidden',
    'actorLockedHint',
    'warnings',
    'warningTemplate',
    'milestoneDate',
    'firstSubLotBanner',
    'inheritedOwner',
    'firstSubLotText',
    'progress',
    'prefilledProgress',
    'progressSummary',
    'progressFrom',
    'undatedHint',
    'earliest',
    'earliestText',
    'delayRow',
  ];

  static values = {
    marcheContractors: Object,
    parentMarche: Object,
    parentAnchor: Object,
    planAnchor: String,
    calendar: Object,
    nonWorkedWarning: String,
    holidayWarning: String,
    periodWarning: String,
    holidayNames: Object,
    monthNames: Array,
    firstOfMonth: String,
    periodDefaultLabel: String,
    zeroDayWarning: String,
    weekdayNames: Array,
    prefillParent: String,
    parentOwner: Object,
    parentPrefill: Object,
    prefillTaken: Object,
    ownAssignment: Boolean,
    incomingLinks: Array,
    shortDayNames: Array,
    delayLabels: Object,
    earliestSentence: String,
    earliestSentenceWithDelay: String,
  };

  // QA-PR2-35: the entreprise label of « Aucun » marché once a « Lot parent » change
  // has rewritten the hidden owner. null = no change yet → the server's
  // `marcheContractors['']` (the pre-filled parent's entreprise) still holds.
  #inheritedOwnerLabel = null;

  // FU-A: the values the form last TOOK from a parent (the « still taken » baseline).
  // null until the first « Lot parent » change, then seeded from `prefillTaken`.
  #taken = null;

  // NR1-01: true while « Fin » holds a value `recomputeEnd` DERIVED from Début + Durée
  // (not a saisie); false once the user sets the Fin, or a parent's Fin is taken.
  #endDerived = false;

  // The « Lot parent » chosen since the first paint (null = still `prefillParent`).
  #chosenParent = null;

  // Edit form only: the « Marché » value this controller last WROTE on a « Lot parent » change (null = none).
  // It is never the user's own choice (`#keepsOwnAssignment`, revue du delta 11/10).
  #writtenMarche = null;

  // The « Premier sous-lot » banner as the server painted it ({ hidden, text }), captured
  // at the first parent change: restored when that parent is chosen again and the map
  // has no sentence for it (a leaf the form cannot choose).
  #firstBanner = null;

  // T6: the delay table and the picker are refreshed through `this.element` (a row
  // cloned by the picker carries no `data-action`); removed in `disconnect`.
  #onDelayInput = (event) => {
    if (event.target?.closest?.('[data-delay-field]')) this.refreshEarliest();
  };

  #onPickerChanged = () => this.refreshEarliest();

  connect() {
    this.#listen('addEventListener');

    // Reconcile the initial render right away (the form may be pre-filled). The
    // server already painted the milestone actor-lock for the INITIAL marché, so
    // we DON'T re-derive it here — re-running the lock from connect would clobber
    // an edit's server-rendered state with no user change. It reconciles only when
    // the user actually changes the marché (the `updateContractor` /
    // `syncMarcheFromParent` actions). The only listeners (T6, above) sit on
    // `this.element` and are removed in `disconnect`.
    // PR C: a server-rendered « Fin » is authoritative and NEVER rewritten on
    // connect (a Mon → Sat phase would silently become Mon → Fri): only an EMPTY
    // « Fin » is derived. The warnings reconcile either way.
    if (this.hasMilestoneDateTarget || (this.hasEndDateTarget && this.endDateTarget.value)) {
      this.#refreshDates();
    } else {
      this.recomputeEnd();
    }
    // Both branches end in `#refreshDates`, which runs `refreshEarliest` (T6).
  }

  disconnect() {
    this.#listen('removeEventListener');
  }

  // Adds or removes the T6 listeners on `this.element`, the same three handlers both
  // ways. Skipped when the element has no event API: the node harness of
  // `planning_phase_form_worked_days.test.mjs` stubs `element` as a bare query object.
  #listen(method) {
    if (typeof this.element[method] !== 'function') return;

    this.element[method]('input', this.#onDelayInput);
    this.element[method]('change', this.#onDelayInput);
    this.element[method]('planning-dependency-picker:changed', this.#onPickerChanged);
  }

  // ── Au plus tôt (T6, see the header) ───────────────────────────────────────

  refreshEarliest() {
    if (!this.hasEarliestTarget) return;

    const requirement = this.#earliestRequirement();
    if (requirement && this.hasEarliestTextTarget) {
      this.earliestTextTarget.replaceChildren(...this.#earliestSentence(requirement));
    }
    this.earliestTarget.hidden = requirement === null;
  }

  // « Caler à cette date »: Début = the proposed start, « Fin » re-derived at constant
  // worked Durée through the existing Durée → Fin path, focus on Début's visible field.
  snapToEarliest() {
    if (!this.hasStartTarget || !this.hasWorkDaysTarget || !this.hasEndDateTarget) return;

    // Recomputed on the click: the fields the notice reads may have moved since.
    const requirement = this.#earliestRequirement();
    if (!requirement) return;

    // A blank « Durée » next to two dates: keep the span they measure.
    const duration = this.#workDays();
    if (!Number.isInteger(parseInt(this.workDaysTarget.value, 10)) && duration !== null) {
      this.workDaysTarget.value = String(duration);
    }
    this.#setIso(this.startTarget, requirement.start);
    this.recomputeEnd();
    this.#datePickerRootFor(this.startTarget)
      ?.querySelector('[data-date-picker-target="display"]')
      ?.focus();
  }

  // The MOST demanding requirement among the incoming links, when the lot starts before
  // it; null otherwise (no Début, nothing constraining, on time).
  #earliestRequirement() {
    if (!this.hasStartTarget) return null;
    const start = this.#isoOrNull(this.startTarget.value);
    if (!start) return null;

    const calendar = this.#calendar();
    let best = null;
    for (const link of this.#constrainingLinks()) {
      const proposed = this.#proposedStart(calendar, link);
      if (proposed && (best === null || proposed > best.start)) best = { ...link, start: proposed };
    }
    if (best === null || calendar.nextWorked(start) >= best.start) return null;
    return best;
  }

  // The start a link proposes: its constrained date for fs / ss, `startFor` of the
  // required end for ff / sf (S1). null when the predecessor lacks the anchor (S2).
  #proposedStart(calendar, link) {
    const required = calendar.constrainedDate(
      link.type,
      this.#isoOrNull(link.predStart),
      this.#isoOrNull(link.predEnd),
      link.delayDays,
      link.delayUnit
    );
    if (!required) return null;
    if (link.type === 'fs' || link.type === 'ss') return required;

    return calendar.startFor(required, Math.max(1, this.#workDays() ?? 1));
  }

  // Visible delay rows (typed values), then the incoming links without any row
  // (milestones, S7). A hidden row (unchecked prerequisite) drops its link.
  #constrainingLinks() {
    const known = new Map(
      (this.incomingLinksValue || []).map((link) => [String(link.predecessorId), link])
    );
    const rowIds = new Set();
    const links = [];
    for (const row of this.delayRowTargets) {
      const id = String(row.dataset.predecessorId ?? '');
      rowIds.add(id);
      if (row.hidden) continue;

      const days = this.#typedDelay(row);
      if (days === null) continue;
      links.push({
        type: known.get(id)?.type || DEFAULT_LINK_TYPE,
        delayDays: days,
        delayUnit: this.#typedUnit(row),
        predecessorLabel: row.dataset.predecessorLabel || known.get(id)?.predecessorLabel || '',
        predStart: row.dataset.predStart,
        predEnd: row.dataset.predEnd,
      });
    }
    known.forEach((link, id) => {
      if (rowIds.has(id)) return;
      links.push({
        type: link.type || DEFAULT_LINK_TYPE,
        delayDays: Number.isInteger(link.delayDays) ? link.delayDays : 0,
        delayUnit: DELAY_UNITS.includes(link.delayUnit) ? link.delayUnit : DEFAULT_DELAY_UNIT,
        predecessorLabel: link.predecessorLabel || '',
        predStart: link.predStart,
        predEnd: link.predEnd,
      });
    });
    return links;
  }

  // The delay typed in a row: empty = 0 (Q-DL-13), « −2 » = -2 (Q-DL-3); anything else
  // (not an integer, or beyond ±DELAY_MAX) → null, the link is left out (the server refuses it).
  #typedDelay(row) {
    const field = row.querySelector('[data-delay-field="days"]');
    const raw = String(field?.value ?? '')
      .trim()
      .split(MINUS_SIGN)
      .join('-');
    if (raw === '') return 0;
    if (!DELAY_PATTERN.test(raw)) return null;
    const days = parseInt(raw, 10);
    return Math.abs(days) <= DELAY_MAX ? days : null;
  }

  #typedUnit(row) {
    const unit = row.querySelector('[data-delay-field="unit"]')?.value;
    return DELAY_UNITS.includes(unit) ? unit : DEFAULT_DELAY_UNIT;
  }

  // The worked « Durée »: the field, else the worked days of [Début, Fin], else null.
  #workDays() {
    const typed = this.hasWorkDaysTarget ? parseInt(this.workDaysTarget.value, 10) : NaN;
    if (Number.isInteger(typed) && typed >= 0) return typed;
    if (!this.hasStartTarget || !this.hasEndDateTarget) return null;

    const start = this.#isoOrNull(this.startTarget.value);
    const end = this.#isoOrNull(this.endDateTarget.value);
    if (!start || !end || end < start) return null;
    return Math.max(0, this.#calendar().workedDaysBetween(start, end));
  }

  // « Au plus tôt le <strong>lun. 19/10/2026</strong>, après Chape +7 j cal. » (maquette
  // planche 1, QA-F7; wording of the retour Nicolas 16/10): every word from the server
  // sentence, split on its placeholders (any order, any language). A NULL delay takes the
  // sentence without `%{delay}` (D18: « (Ragréage 0 j) » read as a delay), any other the
  // sentence with it. The date in a <strong>; the delay notation, from the shared
  // formatter, made unbreakable (its spaces → no-break spaces) so that « +7 j cal. » never
  // wraps at 375 px. Returns the nodes, never markup.
  #earliestSentence(requirement) {
    const [year, month, day] = requirement.start.split('-');
    const weekdayIndex = new Date(`${requirement.start}T00:00:00Z`).getUTCDay();
    const weekday = (this.shortDayNamesValue || [])[weekdayIndex] || '';
    const values = {
      date: `${weekday} ${day}/${month}/${year}`.trim(),
      lot: String(requirement.predecessorLabel ?? ''),
      delay: delayShort(
        requirement.delayDays,
        requirement.delayUnit,
        this.delayLabelsValue
      ).replace(/ /g, NBSP),
    };
    const template =
      requirement.delayDays === 0
        ? this.earliestSentenceValue
        : this.earliestSentenceWithDelayValue;
    return String(template ?? '')
      .split(PLACEHOLDER)
      .filter((token) => token !== '')
      .map((token) => {
        const name = PLACEHOLDER_NAME.exec(token)?.[1];
        if (!name || !Object.prototype.hasOwnProperty.call(values, name)) {
          return document.createTextNode(token);
        }
        if (name !== 'date') return document.createTextNode(values[name]);
        const strong = document.createElement('strong');
        strong.textContent = values.date;
        return strong;
      });
  }

  // Fired on `change` of a milestone's « Date » (RV-40): a milestone has no
  // Durée ↔ Fin sync, only the non-worked warning.
  refreshNonWorked() {
    this.#refreshDates();
  }

  // QA 03/10: the colour picker's preview bar shows the form's « Avancement », rounded
  // once to the integer (like the Gantt), never the mock « 40 % ». Fired on `input` of
  // the number field and of the slider (after `range-input#sync` mirrored / clamped it);
  // also called when the prefill rewrites the progress. Text only (`textContent`).
  refreshProgressPreview() {
    if (!this.hasProgressTarget) return;
    const preview = this.element.querySelector('[data-color-picker-preview-progress]');
    if (!preview) return;

    const value = Number.parseFloat(this.progressTarget.value);
    if (!Number.isFinite(value)) return;
    preview.textContent = `${Math.round(value)}${NBSP}%`;
  }

  // Fired on `change` of the marché select → refresh the entreprise hint.
  updateContractor(event) {
    this.#refreshContractor(event.target.value);
  }

  // Fired on `change` of the « Lot parent » select → re-fill the « Marché » select
  // with the new lot's marché, then refresh the entreprise hint. Setting
  // `<select>.value` does NOT fire a `change` event, so the hint is refreshed
  // DIRECTLY rather than relying on the marché select's own listener.
  syncMarcheFromParent(event) {
    // The « Lot parent » SearchableSelect's SEARCH field fires its own `change` on
    // blur (a typed text, never a lot id): only the hidden value input counts.
    if (event.target.type === 'text') return;

    // FU-A / CR-H4: the entry of the chosen parent (a leaf or a parent lot; undefined for
    // the root and for a lot the form cannot choose).
    const parentValue = String(event.target.value ?? '');
    const entry = (this.parentPrefillValue || {})[parentValue];
    this.#captureFirstBanner();
    // FU-A: BEFORE the re-anchoring, so the « Fin » calendar follows the new « Début ».
    this.#syncParentPrefill(entry);
    this.#chosenParent = parentValue;
    // After the prefill: the banner's sentence depends on the dates it left (NR1-01).
    this.#syncFirstSubLotBanner(parentValue, entry);

    // F3 — re-anchor the calendars FIRST: it must happen even on a form with no
    // « Marché » select (the guard below is about the marché, not about the dates).
    this.reanchorFromParent(event.target.value);

    // QA-PR2-35: the hidden owner follows the NEW parent (else it kept the first one's
    // entreprise and the sub-lot was assigned, and notified, to the wrong firm).
    this.#syncInheritedOwner(event.target.value);

    if (!this.hasMarcheTarget) return;
    // Décision Nicolas 11/10 (PR #796) : un lot DÉPLACÉ qui a déjà un marché ou un intervenant
    // n'hérite pas de ceux du parent ; seul un lot vide hérite. À la création, inchangé.
    if (this.#keepsOwnAssignment()) return;

    // The map keys mirror the rendered parent <option> values (lot id as String);
    // each value is the lot's INHERITED marché (its own, else the first ancestor
    // group's). A lot with none in its chain maps to '' (« Aucun »). Unknown
    // parent → '' as well.
    const marcheValue = (this.parentMarcheValue || {})[event.target.value] ?? '';
    // Edit form: an EMPTY lot that took a marché from a previous parent of this form follows the new one,
    // and becomes empty again under a parent without marché (it was empty).
    if (!this.#isCreation()) {
      this.#writtenMarche = marcheValue;
      this.marcheTarget.value = marcheValue;
      this.#refreshContractor(marcheValue);
      return;
    }
    // D-R-07 (Nicolas, 2026-09-30): a parent WITHOUT a marché leaves « Marché » as
    // it is. Resetting it to « Aucun » cleared the entreprise on save, silently.
    if (marcheValue === '') {
      // « Aucun » kept: its entreprise label is now the new parent's (or none).
      const keptNone = this.marcheTarget.value === '';
      if (this.hasInheritedOwnerTarget && keptNone) this.#refreshContractorHint('');
      return;
    }

    this.marcheTarget.value = marcheValue;
    this.#refreshContractor(marcheValue);
  }

  // S-04 / FU-A / CR-H5: the « Premier sous-lot de … » banner speaks of the CHOSEN leaf,
  // with its server-composed sentence; hidden for any other choice (a parent lot, the
  // root). Every NEW lot form renders it (hidden when no leaf is pre-filled), so choosing
  // a leaf shows it wherever the form was opened. A choice without a map sentence that is
  // the first paint's parent again gets the first paint's banner back. The `hidden`
  // attribute (never a style nor a class written here); the text by `textContent`.
  #syncFirstSubLotBanner(parentValue, entry) {
    if (!this.hasFirstSubLotBannerTarget) return;

    if (entry?.banner) {
      this.firstSubLotBannerTarget.hidden = false;
      this.#refreshFirstSubLotText();
      return;
    }
    const first = this.#firstBanner;
    if (parentValue === this.prefillParentValue && first) {
      if (this.hasFirstSubLotTextTarget) this.firstSubLotTextTarget.textContent = first.text;
      this.firstSubLotBannerTarget.hidden = first.hidden;
      return;
    }
    this.firstSubLotBannerTarget.hidden = true;
  }

  #captureFirstBanner() {
    if (this.#firstBanner || !this.hasFirstSubLotBannerTarget) return;

    const text = this.hasFirstSubLotTextTarget ? this.firstSubLotTextTarget.textContent : '';
    this.#firstBanner = { hidden: this.firstSubLotBannerTarget.hidden, text };
  }

  // NR1-01: « il reprend ses dates » only while BOTH dates are the chosen leaf's; else the
  // server's variant without the dates. Re-run on every date change and parent change.
  #refreshFirstSubLotText() {
    if (!this.hasFirstSubLotBannerTarget || !this.hasFirstSubLotTextTarget) return;
    if (this.firstSubLotBannerTarget.hidden) return;

    const entry = (this.parentPrefillValue || {})[
      this.#chosenParent ?? String(this.prefillParentValue ?? '')
    ];
    if (!entry?.banner) return;

    const datesTaken =
      this.hasStartTarget &&
      this.hasEndDateTarget &&
      this.startTarget.value === (entry.start ?? '') &&
      this.endDateTarget.value === (entry.end ?? '');
    this.firstSubLotTextTarget.textContent = datesTaken
      ? entry.banner
      : entry.bannerNoDates || entry.banner;
  }

  // ── Reprise du parent choisi (FU-A, see the header for the rule) ─────────────

  // Re-apply the prefill of the chosen parent (`entry`, undefined ⇒ nothing taken) to
  // every field that still holds the previous parent's value. No-op off a NEW lot form.
  #syncParentPrefill(entry) {
    if (!this.hasPrefillTakenValue) return;

    const previous = this.#taken ?? this.#prefillFields(this.prefillTakenValue);
    const next = this.#prefillFields(entry ?? NOTHING_TAKEN);
    // CR-H4: a parent lot's entry gives dates and colour, never the QA-PR2-38 value nor
    // « Repris de … » (a second sub-lot is born at 0 %, it takes no progress).
    const leaf = Boolean(entry) && !entry.parent;

    this.#rewriteProgress(previous.progress, next.progress);
    if (this.hasPrefilledProgressTarget) {
      this.prefilledProgressTarget.value = leaf ? next.progress : '';
    }
    this.#rewriteDates(previous, next);
    this.#rewriteColor(previous.color, next.color);
    this.#syncProgressFrom(leaf ? entry : undefined);
    this.#taken = next;
  }

  // `prefillFields` plus `workDays`: the worked days of the taken span ('' without both
  // dates), so that a « Durée » still holding them reads as taken (NR1-01).
  #prefillFields(raw) {
    const fields = prefillFields(raw);
    const start = this.#isoOrNull(fields.start);
    const end = this.#isoOrNull(fields.end);
    const days =
      start && end && end >= start ? this.#calendar().workedDaysBetween(start, end) : null;
    return { ...fields, workDays: days === null ? '' : String(Math.max(0, days)) };
  }

  #rewriteProgress(previous, next) {
    if (!this.hasProgressTarget) return;

    const input = this.progressTarget;
    if (!sameNumber(input.value, previous) || sameNumber(input.value, next)) return;

    input.value = next;
    // `range-input#sync` mirrors the number into the slider (and clamps it).
    input.dispatchEvent(new Event('input', { bubbles: true }));
    if (this.hasProgressSummaryTarget) {
      this.progressSummaryTarget.textContent = `${Math.trunc(Number(next))} %`;
    }
    this.refreshProgressPreview();
  }

  #rewriteDates(previous, next) {
    if (!this.hasStartTarget || !this.hasEndDateTarget) return;

    if (this.#endDerived && this.hasWorkDaysTarget) {
      this.#rewriteDerivedSpan(previous, next);
      return;
    }

    let moved = false;
    for (const [target, key] of [
      [this.startTarget, 'start'],
      [this.endDateTarget, 'end'],
    ]) {
      if (target.value !== previous[key] || target.value === next[key]) continue;

      this.#setIso(target, next[key]);
      moved = true;
    }
    if (!moved) return;

    this.#syncDurationFromDates();
  }

  // `#setIso` silences our own recompute handlers: re-derive « Durée » from the two dates.
  // A lone date blanks it (it measured the previous parent's span). The Fin is not derived.
  #syncDurationFromDates() {
    if (this.startTarget.value && this.endDateTarget.value) {
      this.recomputeDuration();
      return;
    }
    if (this.hasWorkDaysTarget) this.workDaysTarget.value = '';
    this.#endDerived = false;
    this.#refreshDates();
  }

  // NR1-01: « Fin » was DERIVED from Début + Durée (`recomputeEnd`), so the user's input is
  // the Début and/or the Durée, never that Fin. Each of the two still holding the previous
  // parent's value follows the new parent; the Fin is derived again from the result. When
  // neither was typed, the new parent's own span is taken as is (its Fin may sit on a
  // non-worked day, which a derivation would move).
  #rewriteDerivedSpan(previous, next) {
    const startTaken = this.startTarget.value === previous.start;
    const durationTaken = sameDuration(this.workDaysTarget.value, previous.workDays);
    if (startTaken && durationTaken) {
      this.#setIso(this.startTarget, next.start);
      this.#setIso(this.endDateTarget, next.end);
      this.#syncDurationFromDates();
      return;
    }

    const start = startTaken ? next.start : this.startTarget.value;
    const workDays = durationTaken ? next.workDays : this.workDaysTarget.value;
    this.#setIso(this.startTarget, start);
    this.workDaysTarget.value = workDays;

    const iso = this.#isoOrNull(start);
    const days = parseInt(workDays, 10);
    const derived = iso !== null && Number.isInteger(days) && days > 0;
    this.#setIso(this.endDateTarget, derived ? this.#calendar().endFor(iso, days) : '');
    this.#endDerived = derived;
    this.#refreshDates();
  }

  // The `color-picker` is driven through its own public gestures (never its logic):
  // checking a swatch radio + its `change` (= a click), or typing the hex into its
  // « Code couleur » field + `input` for a colour that has no swatch.
  #rewriteColor(previous, next) {
    const picker = this.element.querySelector('[data-controller~="color-picker"]');
    if (!picker) return;

    const radios = Array.from(picker.querySelectorAll('input[type="radio"]'));
    const current = radios.find((radio) => radio.checked);
    if (!current || current.value !== previous || current.value === next) return;

    const swatch = radios.find((radio) => !radio.disabled && radio.value === next);
    if (swatch) {
      swatch.checked = true;
      swatch.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const code = picker.querySelector('[data-color-picker-target="codeInput"]');
    if (!code) return;

    code.value = next;
    code.dispatchEvent(new Event('input', { bubbles: true }));
  }

  // « Repris de … » tells the truth only while the progress holds the chosen leaf's value.
  #syncProgressFrom(entry) {
    if (!this.hasProgressFromTarget) return;

    // `entry` is a LEAF's (the caller passes undefined for a parent lot, CR-H4).
    const shown =
      Boolean(entry) &&
      this.hasProgressTarget &&
      sameNumber(this.progressTarget.value, entry.progress);
    if (shown) this.progressFromTarget.textContent = entry.from ?? '';
    this.progressFromTarget.hidden = !shown;
  }

  // QA-PR2-35: write the new parent's OWN entreprise into the hidden `phase[owner_id]`,
  // '' when it has none or inherits a marché (no key in `parentOwner`). Keys are
  // STRINGS (the builder `.to_s`-es the lot ids, as for `parentMarche`). No-op on an
  // edit or a milestone form (no hidden field).
  #syncInheritedOwner(parentValue) {
    if (!this.hasInheritedOwnerTarget) return;

    const owner = (this.parentOwnerValue || {})[String(parentValue ?? '')];
    this.inheritedOwnerTarget.value = owner?.id ?? '';
    this.#inheritedOwnerLabel = owner?.label ?? '';
  }

  // Edit form only: the lot already carries a marché or an intervenant (`ownAssignment`, server-rendered:
  // an intervenant without a marché has no field here), or the user picked a marché in this form.
  #keepsOwnAssignment() {
    if (this.#isCreation()) return false;

    const value = this.marcheTarget.value;
    return this.ownAssignmentValue || (value !== '' && value !== this.#writtenMarche);
  }

  // The entreprise shown for a marché option value. « Aucun » ('') reads the
  // inherited owner once a parent change rewrote it; every other value is the marché's.
  #contractorNameFor(marcheValue) {
    if (marcheValue === '' && this.#inheritedOwnerLabel !== null) return this.#inheritedOwnerLabel;

    return (this.marcheContractorsValue || {})[marcheValue];
  }

  // ── Re-ancrage des calendriers (F3) ────────────────────────────────────────

  // Called by `syncMarcheFromParent` on a « Lot parent » change (also wireable as
  // a standalone action). Re-points EVERY date field of this form at the newly
  // chosen lot's opening month.
  //
  // ⚠️ CREATION ONLY. On an edit form this returns immediately, so changing an
  // existing phase's parent lot leaves its dates — and even the month its pickers
  // open on — strictly untouched.
  reanchorFromParent(parentValue) {
    if (!this.#isCreation()) return;

    const anchor = this.#anchorForParent(parentValue);
    this.#datePickerRoots().forEach((root) => this.#setAnchor(root, anchor));
    // « Fin » outranks the lot anchor as soon as a « Début » is typed — the blanket
    // pass above would otherwise leave the end calendar on the lot's month until
    // the next « Début » / « Durée » edit (a lot change fires no recompute).
    this.#reanchorEndFromStart();
  }

  // Resolve a « Lot parent » <option> value to its opening ISO date:
  // the lot's own anchor, else the plan's advancement front, else '' (no anchor →
  // the attribute is REMOVED and VanillaCalendar keeps its native default).
  //
  // ⚠️ The map is keyed by STRING — `Plans::LotFormBuilder#parent_anchor` calls
  // `.to_s` on each lot id, mirroring `parent_marche`, because these keys mime the
  // DOM. A `<select>.value` is already a string, so the cast below is a no-op
  // TODAY; it is here because a numeric key (an id read from a dataset, a future
  // caller passing `phase.id`) would miss SILENTLY, fall back to `planAnchor`, and
  // leave every server-side test green. Same trap, same fix, as on the Ruby side.
  // « Aucun » posts '' and is never a key → the plan anchor, as specified.
  #anchorForParent(parentValue) {
    const key = String(parentValue ?? '');
    return (this.parentAnchorValue || {})[key] || this.planAnchorValue || '';
  }

  // The « Fin » calendar follows the « Début » ACTUALLY TYPED (spec F3: « Saisie de
  // la fin → le mois du début saisi »). Called from `recomputeEnd`, i.e. on every
  // start / durée edit.
  //
  // NOT gated on creation, and it cannot rewrite anything: the `date-picker` side
  // ignores an anchor as soon as the field holds an ISO, so on an edit form (both
  // dates filled) this is inert. It only bites when « Fin » is EMPTY — exactly the
  // case where the user is about to pick it. A blank start never blanks the
  // server-rendered anchor (we bail instead of writing '').
  #reanchorEndFromStart() {
    if (!this.hasStartTarget || !this.hasEndDateTarget) return;

    const start = this.startTarget.value;
    if (!start) return;

    this.#setAnchor(this.#datePickerRootFor(this.endDateTarget), start);
  }

  // Write (or clear) a `date-picker` root's anchor. Stimulus reflects the attribute
  // into `anchorValue` and fires the picker's `anchorValueChanged`, which moves the
  // displayed month WITHOUT selecting anything.
  #setAnchor(root, iso) {
    if (!root) return;

    if (iso) root.setAttribute('data-date-picker-anchor-value', iso);
    else root.removeAttribute('data-date-picker-anchor-value');
  }

  // Every `date-picker` root INSIDE this form — the phase pair (« Début » / « Fin »)
  // or the jalon's lone « Date » (`milestone_date`), which carries no
  // `planning-phase-form` target of its own and would otherwise be unreachable.
  // Scoped to `this.element`, never to `document`.
  #datePickerRoots() {
    return Array.from(this.element.querySelectorAll('[data-controller~="date-picker"]'));
  }

  // The `date-picker` root owning a given hidden ISO input. `closest` from one of
  // OUR targets stays inside this form.
  #datePickerRootFor(target) {
    return target.closest('[data-controller~="date-picker"]');
  }

  // True on a CREATE form, false on an EDIT one.
  //
  // Rails renders the `_method` override input ONLY for a verb that is neither GET
  // nor POST, and `Planning::LotFormChrome#http_method` yields :post for a new
  // form and :patch for an edit one. So the PRESENCE of `input[name="_method"]`
  // inside this form IS the edit signature — no extra server flag to keep in sync,
  // and no reliance on a URL shape.
  #isCreation() {
    return !this.element.querySelector('input[name="_method"]');
  }

  // The hub fired by `updateContractor` AND `syncMarcheFromParent` whenever the
  // marché changes. Refreshes the two marché-driven surfaces in lock-step:
  //   1. the « Entreprise : <nom> » info hint (the original Feedback Gantt #38 job);
  //   2. (jalon) the AUTONOMOUS « Acteur » select lock — derived from + locked to
  //      the marché's entreprise when a marché is chosen, freed when it's cleared.
  // Step 2 is a no-op on a phase/group form (its targets are absent).
  // Visibility is driven by inline `display` (not the `hidden` class — Tailwind v4
  // cascade swallows it; see the project memory note).
  #refreshContractor(marcheValue) {
    this.#refreshContractorHint(marcheValue);
    this.#lockActorToMarche(marcheValue);
  }

  // Reveal + fill the « Entreprise » hint when the given marché value carries an
  // entreprise, hide it otherwise (« Aucun » or a contractor-less marché → no name
  // to show).
  #refreshContractorHint(marcheValue) {
    if (!this.hasContractorBoxTarget || !this.hasContractorNameTarget) return;

    const name = this.#contractorNameFor(marcheValue);
    if (name) {
      this.contractorNameTarget.textContent = name;
      this.contractorBoxTarget.style.display = '';
    } else {
      this.contractorNameTarget.textContent = '';
      this.contractorBoxTarget.style.display = 'none';
    }
  }

  // (jalon) Lock / unlock the AUTONOMOUS « Acteur » select to the chosen marché.
  //   • marché chosen  → derive the actor from the marché's entreprise: disable the
  //     select, set it (and the hidden POST mirror) to that entreprise's value, and
  //     reveal the « Acteur dérivé du marché » hint. A disabled <select> is NOT
  //     POSTed, so the hidden field is what actually carries phase[owner_id].
  //   • marché cleared → re-enable the select, reset it to « Aucun », blank the
  //     hidden mirror, and hide the hint.
  // The entreprise's actor option value is looked up by NAME among the select's
  // options (the markup has no actor→role/value map — see the contract note); the
  // hidden mirror tracks the resolved value so the right owner POSTs either way.
  #lockActorToMarche(marcheValue) {
    if (!this.hasActorSelectTarget) return;

    const contractorName = (this.marcheContractorsValue || {})[marcheValue];

    if (marcheValue && contractorName) {
      const value = this.#actorValueForName(contractorName);
      this.actorSelectTarget.disabled = true;
      this.actorSelectTarget.value = value;
      if (this.hasActorHiddenTarget) this.actorHiddenTarget.value = value;
      if (this.hasActorLockedHintTarget) this.actorLockedHintTarget.style.display = '';
    } else {
      this.actorSelectTarget.disabled = false;
      this.actorSelectTarget.value = '';
      if (this.hasActorHiddenTarget) this.actorHiddenTarget.value = '';
      if (this.hasActorLockedHintTarget) this.actorLockedHintTarget.style.display = 'none';
    }
  }

  // Resolve a marché entreprise NAME to the matching « Acteur » option value, by
  // scanning the select's option labels (the markup gives no name→value map). The
  // marché's entreprise is always one of the actor options, so a match is expected;
  // an unexpected miss falls back to '' (« Aucun »), which the server re-derives
  // from the marché anyway (§5.2bis — marché has priority over owner_id).
  #actorValueForName(name) {
    if (!this.hasActorSelectTarget) return '';

    const match = Array.from(this.actorSelectTarget.options).find(
      (option) => option.value !== '' && option.textContent.trim() === name
    );
    return match ? match.value : '';
  }

  // Début / Durée → Fin. « Fin » = the Durée-th WORKED day from « Début » inclusive.
  // `event` is the `change` of « Début » or the `input` of « Durée » (none on connect).
  recomputeEnd(event) {
    if (this._suppress) return;
    if (!this.hasStartTarget || !this.hasWorkDaysTarget || !this.hasEndDateTarget) return;

    // F3 — BEFORE any write: when the branch below BLANKS « Fin », the picker
    // resyncs the emptied field onto its anchor, and that anchor must already be
    // the « Début » the user just typed.
    this.#reanchorEndFromStart();

    const start = this.#isoOrNull(this.startTarget.value);
    const workDays = parseInt(this.workDaysTarget.value, 10);

    // RV-1: Durée 0 = a phase entirely on non-worked days; its existing « Fin »
    // stays (the server takes the posted Fin), it is never blanked. A Début moved
    // PAST that Fin pulls the Fin back onto it (the save would refuse Fin < Début),
    // as `recomputeDuration` does, and the Durée follows.
    if (start && workDays === 0 && this.#isoOrNull(this.endDateTarget.value)) {
      if (this.#isoOrNull(this.endDateTarget.value) < start) {
        this.#setIso(this.endDateTarget, start);
        this.workDaysTarget.value = String(
          Math.max(0, this.#calendar().workedDaysBetween(start, start))
        );
      }
      this.#refreshDates();
      return;
    }

    // CR-H1: « Début » emptied ALONE keeps « Fin » and « Durée » (it used to blank both,
    // and the server, which accepts a lot with no date at all, silently unscheduled it).
    // The save is now refused (« Date de début doit être remplie »). The lot becomes « à
    // planifier » only when the user empties Début AND Fin: then the Durée goes too
    // (QA-PR2-37: it would read as a date; the server ignores it anyway). Only on the
    // Début's own change: a Durée typed BEFORE the Début stays.
    if (!start) {
      const endEmpty = !this.#isoOrNull(this.endDateTarget.value);
      if (endEmpty && event?.target === this.startTarget) this.workDaysTarget.value = '';
      this.#refreshDates();
      return;
    }

    // A Début without a usable Durée: no Fin can be derived.
    if (!Number.isInteger(workDays) || workDays <= 0) {
      this.#setIso(this.endDateTarget, '');
      this.#endDerived = false;
      this.#refreshDates();
      return;
    }

    this.#setIso(this.endDateTarget, this.#calendar().endFor(start, workDays));
    this.#endDerived = true;
    this.#refreshDates();
  }

  // Fin → Durée. « Durée » = worked days of [Début, Fin], bounds included, floor 0
  // (RV-1: a weekend-only span reads 0, never a forced 1). An earlier Fin snaps back
  // to Début so the span is never negative; the server re-validates « Fin ≥ Début ».
  recomputeDuration() {
    if (this._suppress) return;
    if (!this.hasStartTarget || !this.hasWorkDaysTarget || !this.hasEndDateTarget) return;

    // The Fin is the user's (or a parent's), never a derived one (NR1-01).
    this.#endDerived = false;
    const start = this.#isoOrNull(this.startTarget.value);
    let end = this.#isoOrNull(this.endDateTarget.value);
    // QA-PR2-37: both dates emptied → « à planifier », the Durée goes with them.
    if (!start && !end) {
      this.workDaysTarget.value = '';
      this.#refreshDates();
      return;
    }
    if (!start || !end) {
      this.#refreshDates(); // nothing to sync until both dates are valid
      return;
    }

    // ISO `YYYY-MM-DD` strings order like the dates they carry.
    if (end < start) {
      end = start;
      this.#setIso(this.endDateTarget, end);
    }

    const days = this.#calendar().workedDaysBetween(start, end);
    this.workDaysTarget.value = String(Math.max(0, days));
    this.#refreshDates();
  }

  // The site's FULL calendar (mask, holidays off, periods), absent value → {} →
  // lundi-vendredi without exception (RF-PM8, see the module).
  #calendar() {
    return workCalendarFrom(this.calendarValue);
  }

  // Everything that reads the two dates, after any change of them: the « Sans dates »
  // help, the banner's sentence (NR1-01), the non-worked warnings, the « au plus tôt »
  // notice (T6).
  #refreshDates() {
    this.#refreshUndatedHint();
    this.#refreshFirstSubLotText();
    this.#refreshWarnings();
    this.refreshEarliest();
  }

  // QA 03/10 (7a): « Sans dates, le lot est à planifier. » only while Début AND Fin are
  // empty (it showed on every edit, dated lots included). `hidden` attribute only.
  #refreshUndatedHint() {
    if (!this.hasUndatedHintTarget || !this.hasStartTarget || !this.hasEndDateTarget) return;

    this.undatedHintTarget.hidden = Boolean(this.startTarget.value || this.endDateTarget.value);
  }

  // One warning line per DISTINCT date posed on a non-worked day (Début, then
  // Fin — or a milestone's single Date), then « 0 jour travaillé » for a phase whose
  // Début → Fin holds no worked day (SS2); rebuilt from the server <template>, each
  // field wrapper's `data-non-worked` follows. No-op without the warnings target
  // (older markup, node harnesses).
  #refreshWarnings() {
    if (!this.hasWarningsTarget || !this.hasWarningTemplateTarget) return;

    const calendar = this.#calendar();
    const flagged = [];
    for (const target of this.#dateTargets()) {
      const iso = this.#isoOrNull(target.value);
      const nonWorked = iso !== null && !calendar.isWorked(iso);
      const wrapper = target.closest('[data-non-worked]');
      if (wrapper) wrapper.dataset.nonWorked = String(nonWorked);
      if (nonWorked && !flagged.includes(iso)) flagged.push(iso);
    }
    const lines = flagged.map((iso) => this.#warningLine(this.#nonWorkedSentence(calendar, iso)));
    if (this.#zeroWorkedDays(calendar)) lines.push(this.#warningLine(this.zeroDayWarningValue));
    this.warningsTarget.replaceChildren(...lines);
  }

  #dateTargets() {
    return [
      this.hasStartTarget && this.startTarget,
      this.hasEndDateTarget && this.endDateTarget,
      this.hasMilestoneDateTarget && this.milestoneDateTarget,
    ].filter(Boolean);
  }

  // Same rule as `LotFormComponent#zero_worked_days?`: a phase (never a milestone)
  // with both dates, Fin ≥ Début, and no worked day between them.
  #zeroWorkedDays(calendar) {
    if (!this.hasStartTarget || !this.hasEndDateTarget) return false;
    const start = this.#isoOrNull(this.startTarget.value);
    const end = this.#isoOrNull(this.endDateTarget.value);
    return (
      start !== null && end !== null && end >= start && calendar.workedDaysBetween(start, end) === 0
    );
  }

  // SS10: the sentence follows WHY the day is off, one reason by priority
  // (`offReason`: outside the mask > holiday off > period), each from its own raw
  // server template (no text in the JS). A holiday or a period reads its date
  // without the year (« 1er mai », « 5 août »); a template the markup does not carry
  // falls back to the weekly sentence, never to an empty line.
  #nonWorkedSentence(calendar, iso) {
    const reason = calendar.offReason(iso);
    if (reason?.kind === 'holiday' && this.holidayWarningValue) {
      return fillPlaceholders(this.holidayWarningValue, {
        date: this.#dayAndMonth(iso),
        name: this.holidayNamesValue[reason.key] ?? '',
      });
    }
    if (reason?.kind === 'period' && this.periodWarningValue) {
      return fillPlaceholders(this.periodWarningValue, {
        date: this.#dayAndMonth(iso),
        label: calendar.periodOn(iso)?.label || this.periodDefaultLabelValue,
      });
    }
    const [year, month, day] = iso.split('-');
    return fillPlaceholders(this.nonWorkedWarningValue, {
      date: `${day}/${month}/${year}`,
      weekday: this.weekdayNamesValue[new Date(`${iso}T00:00:00Z`).getUTCDay()] || '',
    });
  }

  // « 1er mai » / « 5 août »: read from the ISO string (no `Date`, no time zone), the
  // month from the locale's `month_names`, day 1 as the locale's ordinal.
  #dayAndMonth(iso) {
    const [, month, day] = iso.split('-').map(Number);
    const dayText = day === 1 && this.firstOfMonthValue ? this.firstOfMonthValue : String(day);
    return `${dayText} ${this.monthNamesValue[month] || ''}`.trim();
  }

  #warningLine(text) {
    const line = this.warningTemplateTarget.content.firstElementChild.cloneNode(true);
    line.querySelector('[data-warning-text]').textContent = text;
    return line;
  }

  // Write an ISO value to a hidden date-picker field, then dispatch a self-suppressed
  // bubbling `change` so the sibling `date-picker` controller re-syncs its FR display +
  // popup (its `#onHiddenChange`). `_suppress` makes our OWN change a no-op for our
  // recompute handlers (mirrors the picker's `_internalWrite` idiom). No-op when unchanged.
  #setIso(target, iso) {
    if (target.value === iso) return;
    target.value = iso;
    this._suppress = true;
    target.dispatchEvent(new Event('change', { bubbles: true }));
    this._suppress = false;
  }

  // A valid `yyyy-mm-dd` value, or null. The calendar module works on ISO strings
  // in UTC-midnight arithmetic: no local `Date` ever crosses into it.
  #isoOrNull(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return null;
    return Number.isNaN(Date.parse(`${value}T00:00:00Z`)) ? null : value;
  }
}
